"use client"

import { useRef } from "react"
import Image from "next/image"

/* Live-sites slider for the web-development #samples section: a scroll-snap
   track showing 3 cards at desktop (2 tablet, 1 mobile) with prev/next arrows.
   Zero dependencies on purpose (no swiper/embla — CLAUDE.md bans new runtime
   deps); native scrolling gives touch swipe for free and the fixed aspect
   boxes keep CLS at 0.

   Advances only on interaction — autoplay was removed (Karol, Sept 2026), so
   there is no timer, no IntersectionObserver, and nothing to pause: the
   WCAG 2.2.2 question never arises. */

export type LiveSite = {
  name: string
  site: string
  href: string
  stack: string
  note: string
  image: { src: string; alt: string }
}

export function LiveSiteSlider({ sites }: { sites: LiveSite[] }) {
  const trackRef = useRef<HTMLUListElement>(null)

  const go = (dir: 1 | -1, smooth = true) => {
    const track = trackRef.current
    if (!track) return
    const behavior: ScrollBehavior = smooth ? "smooth" : "auto"
    const max = track.scrollWidth - track.clientWidth
    const step =
      track.children.length > 1
        ? (track.children[1] as HTMLElement).offsetLeft -
          (track.children[0] as HTMLElement).offsetLeft
        : track.clientWidth
    if (dir > 0 && track.scrollLeft >= max - 8) {
      track.scrollTo({ left: 0, behavior })
    } else if (dir < 0 && track.scrollLeft <= 8) {
      track.scrollTo({ left: max, behavior })
    } else {
      track.scrollBy({ left: dir * step, behavior })
    }
  }


  const reduced = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const arrow = (dir: 1 | -1, label: string, path: string) => (
    <button
      type="button"
      aria-label={label}
      onClick={() => go(dir, !reduced())}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-tk-teal/40 text-tk-teal transition-colors hover:bg-tk-teal hover:text-tk-linen"
    >
      <svg
        viewBox="0 0 20 20"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={path} />
      </svg>
    </button>
  )

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label="Live production sites"
    >
      <div className="mb-4 flex justify-end gap-2">
        {arrow(-1, "Previous sites", "M12.5 4.5 7 10l5.5 5.5")}
        {arrow(1, "Next sites", "M7.5 4.5 13 10l-5.5 5.5")}
      </div>
      <ul
        ref={trackRef}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sites.map((item, index) => (
          <li
            key={item.site}
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${sites.length}`}
            className="w-[85%] shrink-0 snap-start sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]"
          >
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-tk-slate/10 bg-white/60 shadow-sm transition-all hover:border-tk-teal/30 hover:shadow-md"
            >
              <div className="flex items-center gap-1.5 border-b border-tk-slate/10 bg-tk-linen px-4 py-2.5">
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-tk-slate/25" />
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-tk-slate/25" />
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-tk-slate/25" />
                <span className="ml-2 truncate rounded-full bg-white px-3 py-0.5 text-[11px] text-tk-slate/70 font-body">
                  {item.site}
                </span>
              </div>
              <div className="relative aspect-[16/10] border-b border-tk-slate/10">
                <Image
                  src={item.image.src}
                  alt={item.image.alt}
                  fill
                  sizes="(max-width: 640px) 85vw, (max-width: 1024px) 50vw, 33vw"
                  loading="lazy"
                  className="object-cover object-top"
                />
              </div>
              <div className="flex flex-1 flex-col p-6 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-ui text-[11px] font-semibold tracking-[0.22em] text-tk-teal">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="rounded-full border border-tk-slate/20 px-2.5 py-0.5 text-[11px] text-tk-slate/70 font-ui">
                    {item.stack}
                  </span>
                </div>
                <div className="mt-4 space-y-1.5">
                  <span className="font-display text-xl font-semibold tracking-tight text-tk-onyx lg:text-lg">
                    {item.name}
                  </span>
                  <p className="font-body text-sm leading-relaxed text-tk-slate/80">
                    {item.note}
                  </p>
                </div>
                <span className="mt-auto flex items-baseline gap-1.5 pt-6 font-ui text-sm font-semibold text-tk-teal group-hover:underline">
                  Open the live site
                  <span
                    aria-hidden="true"
                    className="shrink-0 transition-transform duration-200 group-hover:translate-x-1"
                  >
                    →
                  </span>
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
